import type { User } from "oidc-client-ts";
import jwt_decode from "jwt-decode";
import { defineStore } from "pinia";
import ApiService from "@/services/apiService";
import { AccessibleCommandType } from "@/utils/AccessibleCommandType";

export interface IUserProfile {
  userName: string;
  email: string;
  cvr: string;
  organizationName: string;
}

export class UserProfile implements IUserProfile {
  public userName: string;
  public email: string;
  public cvr: string;
  public organizationName: string;

  private _effectiveRoles: string[] = [];
  private _claimsKey = "https://oes.dk/identity/bevillingslovsystemer/claims/";

  constructor(user: User) {
    this.userName = this.getInfo(user, "username");
    this.email = this.getInfo(user, "emailaddress");
    this.cvr = this.getInfo(user, "cvrnumber");
    this.organizationName = this.getInfo(user, "organizationname");
    this._effectiveRoles = this.getInfo(user, "role") ?? [];
  }

  private getInfo(user: User, key: string) {
    const claims = this.getUserClaims(user);
    return claims
      ? claims.find((e: any) => e[0] === `${this._claimsKey}${key}`)?.[1]
      : null;
  }
  private getUserClaims(user: User): any {
    if (!user) {
      return null;
    }
    const decoded: any = jwt_decode(user.access_token);
    return decoded ? Object.entries(decoded) : [];
  }
}

interface IProfileState {
  authenticated: boolean;
  user: IUserProfile;
  _accessibleCommand: AccessibleCommandType | null;
  _accessControlPromise: Promise<void> | null;
}

export const useProfileStore = defineStore("profile", {
  state: (): IProfileState => {
    return {
      authenticated: false,
      user: {
        userName: "",
        email: "",
        cvr: "",
        organizationName: "",
      },
      _accessibleCommand: null,
      _accessControlPromise: null,
    };
  },
  getters: {
    isAuthenticated: (state) => state.authenticated,
    userName: (state) => state.user.userName,
    email: (state) => state.user.email,
    cvr: (state) => state.user.cvr,
    organizationName: (state) => state.user.organizationName,
    accessibleCommand: (state) => state._accessibleCommand,
  },
  actions: {
    //Init store data
    setProfile(profile: IProfileState): void {
      this.authenticated = profile ? profile.authenticated : false;
      this.user = profile?.user;
    },
    setAuthenticatedFalse(): void {
      this.authenticated = false;
    },
    async setAccessControl(): Promise<void> {
      if (this._accessControlPromise) {
        return this._accessControlPromise;
      }
      this._accessControlPromise = (async () => {
        try {
          const userProfieViewModel = await ApiService.UserRoles.GetUserProfile();
          this._accessibleCommand = new AccessibleCommandType(userProfieViewModel);
        } finally {
          this._accessControlPromise = null;
        }
      })();
      return this._accessControlPromise;
    },
  },
});
